import React, { useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import sr from '@utils/sr';
import { srConfig } from '@config';
import { usePrefersReducedMotion } from '@hooks';

const StyledPublicationsSection = styled.section`
  max-width: 700px;
  padding: 70px 0 100px;

  .publications-list {
    ${({ theme }) => theme.mixins.resetList};
    margin: 0;
  }
`;

const StyledPublication = styled.li`
  position: relative;
  padding: 25px;
  margin-bottom: 20px;
  border-radius: var(--border-radius);
  background-color: var(--light-navy);
  transition: var(--transition);

  @media (prefers-reduced-motion: no-preference) {
    &:hover,
    &:focus-within {
      transform: translateY(-5px);
      ${({ theme }) => theme.mixins.boxShadow};
    }
  }

  .pub-top {
    ${({ theme }) => theme.mixins.flexBetween};
    align-items: center;
    margin-bottom: 12px;
    gap: 15px;
  }

  .pub-venue {
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
  }

  .pub-badge {
    flex-shrink: 0;
    padding: 4px 10px;
    border: 1px solid var(--green);
    border-radius: var(--border-radius);
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
    line-height: 1;
    white-space: nowrap;
  }

  .pub-title {
    margin: 0 0 10px;
    color: var(--lightest-slate);
    font-size: var(--fz-xl);
    line-height: 1.3;

    a {
      ${({ theme }) => theme.mixins.link};
    }
  }

  .pub-authors {
    margin: 0;
    color: var(--light-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
    line-height: 1.6;

    .me {
      color: var(--white);
      font-weight: 600;
    }
  }
`;

const renderAuthors = authors => {
  if (!authors) {
    return null;
  }
  return authors.split(',').map((name, i, arr) => {
    const trimmed = name.trim();
    const isMe = /gan/i.test(trimmed);
    return (
      <React.Fragment key={i}>
        {isMe ? <span className="me">{trimmed}</span> : trimmed}
        {i < arr.length - 1 ? ', ' : ''}
      </React.Fragment>
    );
  });
};

const Publications = () => {
  const data = useStaticQuery(graphql`
    query {
      publications: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/publications/" } }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              venue
              badge
              authors
              external
            }
          }
        }
      }
    }
  `);

  const publications = data.publications.edges.filter(({ node }) => node);
  const revealPublications = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    revealPublications.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, [prefersReducedMotion]);

  return (
    <StyledPublicationsSection id="publications" aria-label="Publications">
      <ol className="publications-list">
        {publications &&
          publications.map(({ node }, i) => {
            const { title, venue, badge, authors, external } = node.frontmatter;

            return (
              <StyledPublication key={i} ref={el => (revealPublications.current[i] = el)}>
                <div className="pub-top">
                  <span className="pub-venue">{venue}</span>
                  {badge && <span className="pub-badge">{badge}</span>}
                </div>

                <h2 className="pub-title">
                  {external ? (
                    <a href={external} target="_blank" rel="noreferrer">
                      {title}
                    </a>
                  ) : (
                    title
                  )}
                </h2>

                <p className="pub-authors">{renderAuthors(authors)}</p>
              </StyledPublication>
            );
          })}
      </ol>
    </StyledPublicationsSection>
  );
};

export default Publications;
